/**
 * Client-Safe Error Logging Utility
 *
 * This module provides error logging for CLIENT COMPONENTS ONLY.
 * It has NO server-side dependencies and can be safely imported in 'use client' files.
 *
 * For Server Actions and API Routes, use error-logger.ts instead.
 *
 * @see error-logger.ts - Server-side error logging with auth context
 */

/**
 * Sanitize metadata to remove PII (emails, phone numbers, tokens)
 */
function sanitizeMetadata(metadata?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!metadata) return undefined;

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(metadata)) {
    // Redact sensitive fields
    if (
      key.toLowerCase().includes('password') ||
      key.toLowerCase().includes('token') ||
      key.toLowerCase().includes('secret') ||
      key.toLowerCase().includes('api_key') ||
      key.toLowerCase().includes('apikey')
    ) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Redact email values
    if (typeof value === 'string' && value.includes('@')) {
      sanitized[key] = '[EMAIL_REDACTED]';
      continue;
    }

    // Redact phone numbers (simple pattern)
    if (typeof value === 'string' && /^\+?\d{10,15}$/.test(value.replace(/[\s()-]/g, ''))) {
      sanitized[key] = '[PHONE_REDACTED]';
      continue;
    }

    sanitized[key] = value;
  }

  return sanitized;
}

/**
 * Format error for logging - handles various error types
 */
function formatError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }

  if (error && typeof error === 'object' && 'message' in error) {
    // Supabase/PostgrestError objects have .message but aren't Error instances
    const supabaseError = error as {
      message: string;
      code?: string;
      details?: string;
      hint?: string;
    };
    let message = supabaseError.message;
    if (supabaseError.code) message = `[${supabaseError.code}] ${message}`;
    if (supabaseError.details) message += ` | Details: ${supabaseError.details}`;
    if (supabaseError.hint) message += ` | Hint: ${supabaseError.hint}`;
    return { message };
  }

  return { message: String(error) };
}

/**
 * Client-safe error logging function
 *
 * Logs errors from client components with consistent formatting.
 * In production with Sentry, errors are sent to Sentry.
 * In development, errors are logged to console.
 *
 * @param componentName - Name of the component where error occurred
 * @param error - Error object or string
 * @param metadata - Additional metadata (optional, will be sanitized)
 *
 * @example
 * ```typescript
 * "use client";
 *
 * import { logClientError } from '@/lib/utils/error-logger-client';
 *
 * export function MyComponent() {
 *   const handleClick = async () => {
 *     try {
 *       await riskyOperation();
 *     } catch (error) {
 *       logClientError('MyComponent.handleClick', error);
 *     }
 *   };
 * }
 * ```
 */
export function logClientError(
  componentName: string,
  error: unknown,
  metadata?: Record<string, unknown>
): void {
  // NEXT_REDIRECT is Next.js control-flow, not a real error. Filter before Sentry.
  // The catch blocks in onboarding forms also guard this, but this is the backstop
  // for any other client component that catches a leaked NEXT_REDIRECT.
  if (
    error instanceof Error &&
    ((error as Error & { digest?: string }).digest?.startsWith('NEXT_REDIRECT') ||
      error.message === 'NEXT_REDIRECT')
  ) {
    return;
  }

  const { message, stack } = formatError(error);
  const sanitizedMetadata = sanitizeMetadata(metadata);
  const action = `[Client] ${componentName}`;

  if (process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_SENTRY_DSN) {
    // Production with Sentry configured: Send to Sentry
    import('@sentry/nextjs')
      .then((Sentry) => {
        // Convert non-Error objects to proper Error instances for Sentry
        // This prevents "Object captured as exception with keys: ..." messages
        // when logging Supabase/PostgrestError objects which have {code, details, hint, message}
        let errorToCapture: Error;
        if (error instanceof Error) {
          errorToCapture = error;
        } else {
          // Create a proper Error with the formatted message
          errorToCapture = new Error(message);
          errorToCapture.name = 'ClientError';
          // Preserve original error data in cause (ES2022+)
          if (error && typeof error === 'object') {
            (errorToCapture as Error & { cause?: unknown }).cause = error;
          }
        }

        Sentry.captureException(errorToCapture, {
          tags: { action },
          extra: {
            ...sanitizedMetadata,
            // Include original error structure for debugging
            originalError: error instanceof Error ? undefined : error,
          },
        });
      })
      .catch(() => {
        // Silently fail if Sentry import fails - avoid console.error in production
      });
  } else if (process.env.NODE_ENV === 'development') {
    // Development only: Console logging for debugging
    console.error(action, message, { metadata: sanitizedMetadata, stack });
  }
  // Production without Sentry: silently skip (no console.error)
}
